import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Stack,
  Chip,
  IconButton,
  Alert,
  TextField,
  InputAdornment,
  Pagination,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  MusicNote as MusicIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { usePerformersApi } from '@/context/AppContext';
import { PerformerScope } from './PerformerScope';
import { SharedPerformerList } from './SharedPerformerList';
import { ResponsiveCardGrid } from '@/components/ResponsiveCardGrid';
import type { PerformerList as PerformerListType } from 'api-client/models';

export const PerformerList: React.FC = () => {
  const [performers, setPerformers] = useState<PerformerListType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [directory, setDirectory] = useState(false);
  const [submittedSearch, setSubmittedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [revision, setRevision] = useState(0);
  const navigate = useNavigate();
  const performersApi = usePerformersApi();

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setPerformers([]);
    performersApi
      .performersList({ directory, search: submittedSearch, page })
      .then((response) => {
        if (cancelled) return;
        setPerformers(response.results || []);
        // The API uses a fixed page size; retain it on the final partial page.
        if (page === 1)
          setPageCount(
            Math.max(
              1,
              Math.ceil(response.count / (response.results?.length || 1)),
            ),
          );
        setError(null);
      })
      .catch(() => {
        if (!cancelled)
          setError('Failed to load performers. Please try again.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [directory, submittedSearch, page, performersApi, revision]);

  const handleSearch = () => {
    setPage(1);
    setSubmittedSearch(searchQuery.trim());
  };

  const handleDelete = async (performerId: string) => {
    if (!confirm('Are you sure you want to delete this performer?')) {
      return;
    }

    try {
      await performersApi.performersDestroy({ id: performerId });
      setRevision((value) => value + 1);
    } catch {
      alert('Failed to delete performer. Please try again.');
    }
  };

  return (
    <Box sx={{ pb: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" gutterBottom>
            Performers
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {directory
              ? 'Search shared artist profiles. Viewing a profile does not add it to your team.'
              : 'Performers involved in your team’s holds and shows, including past bookings.'}
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/performers/new')}
        >
          New Performer
        </Button>
      </Stack>

      <PerformerScope
        directory={directory}
        onChange={(value) => {
          setDirectory(value);
          setSearchQuery('');
          setSubmittedSearch('');
          setPage(1);
        }}
      />
      <Box mb={3}>
        <TextField
          fullWidth
          label={
            directory
              ? 'Search shared performers'
              : 'Search your team’s performers'
          }
          placeholder="Search by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
            endAdornment: searchQuery && (
              <InputAdornment position="end">
                <Button onClick={handleSearch}>Search</Button>
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => {
                    setSearchQuery('');
                    setSubmittedSearch('');
                    setPage(1);
                  }}
                >
                  ×
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress aria-label="Loading performers" />
        </Stack>
      ) : performers.length === 0 ? (
        <Card>
          <CardContent>
            <Stack alignItems="center" spacing={2} py={4}>
              <MusicIcon sx={{ fontSize: 64, color: 'text.secondary' }} />
              <Typography variant="h6" color="text.secondary">
                No performers found
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {directory
                  ? 'Search for an artist or create a shared profile.'
                  : 'Add a performer to a hold or dated show to include them here.'}
              </Typography>
              <Button
                variant="contained"
                startIcon={directory ? <AddIcon /> : <SearchIcon />}
                onClick={() => {
                  if (directory) navigate('/performers/new');
                  else {
                    setDirectory(true);
                    setSearchQuery('');
                    setSubmittedSearch('');
                    setPage(1);
                  }
                }}
              >
                {directory
                  ? 'Create shared profile'
                  : 'Search shared directory'}
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : directory ? (
        <SharedPerformerList performers={performers} onDelete={handleDelete} />
      ) : (
        <ResponsiveCardGrid>
          {performers.map((performer) => (
            <Card
              key={performer.id}
              sx={{ display: 'flex', flexDirection: 'column' }}
            >
              <CardContent sx={{ flexGrow: 1 }}>
                <Stack spacing={2}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="start"
                    spacing={1}
                  >
                    <Box
                      sx={{ minWidth: 0, flex: 1, overflowWrap: 'anywhere' }}
                    >
                      <Typography variant="h6" gutterBottom>
                        {performer.performerName}
                      </Typography>
                      {performer.primaryCategory && (
                        <Chip
                          label={performer.primaryCategory}
                          size="small"
                          sx={{ mb: 1 }}
                        />
                      )}
                    </Box>
                    <Chip
                      label={`${Number(performer.profileCompletion)}%`}
                      color={
                        Number(performer.profileCompletion) >= 80
                          ? 'success'
                          : Number(performer.profileCompletion) >= 50
                            ? 'warning'
                            : 'default'
                      }
                      size="small"
                      sx={{ flexShrink: 0 }}
                    />
                  </Stack>

                  {performer.tags && performer.tags.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      gap={0.5}
                    >
                      {performer.tags
                        .slice(0, 3)
                        .map((tag: string, index: number) => (
                          <Chip
                            key={index}
                            label={tag}
                            size="small"
                            variant="outlined"
                          />
                        ))}
                      {performer.tags.length > 3 && (
                        <Chip
                          label={`+${performer.tags.length - 3}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Stack>
                  )}

                  <Stack direction="row" spacing={1} alignItems="center">
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {performer.mediaCount} media file
                      {Number(performer.mediaCount) !== 1 ? 's' : ''}
                    </Typography>
                  </Stack>
                </Stack>
              </CardContent>
              <CardActions
                disableSpacing
                sx={{
                  flexWrap: 'wrap',
                  gap: 1,
                  '& > button': { minHeight: 44, minWidth: 44 },
                }}
              >
                <Button
                  size="small"
                  startIcon={<PersonIcon />}
                  onClick={() => navigate(`/performers/${performer.id}`)}
                >
                  Details
                </Button>
                <Button
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => navigate(`/performers/${performer.id}/edit`)}
                >
                  Edit
                </Button>
                <IconButton
                  size="small"
                  color="error"
                  aria-label={`Delete ${performer.performerName}`}
                  onClick={() => handleDelete(performer.id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </CardActions>
            </Card>
          ))}
        </ResponsiveCardGrid>
      )}
      {!loading && pageCount > 1 && (
        <Pagination
          count={pageCount}
          page={page}
          onChange={(_, next) => setPage(next)}
          aria-label="Performer pages"
          sx={{ mt: 3 }}
        />
      )}
    </Box>
  );
};
